/**
 * MarkupCanvas: SVG overlay for rendering markup shapes
 * Feature: 049-critique-photo-markup
 *
 * Renders shapes with percentage-based coordinates using SVG viewBox="0 0 100 100"
 * for resolution-independent rendering across different photo sizes
 */

import React, { useRef } from 'react';
import type { MarkupShape } from '@/types/markup.types';
import { getShapeHandles, renderShape, getShapeZOrder } from '@/utils/markupTools';
import { useMarkupDrag } from '@/hooks/useMarkupDrag';
import { transformShapeForViewBox } from '@/utils/markupRenderer';

interface MarkupCanvasProps {
  shapes: MarkupShape[];
  selectedShapeId: number | null;
  onShapeClick?: (shapeNumber: number) => void;
  onBackgroundClick?: () => void;
  onShapeMove?: (shapeNumber: number, shape: MarkupShape) => void;
  readOnly?: boolean;
  photoAspectRatio?: number; // width / height
}

/**
 * MarkupCanvas component renders shapes on an SVG overlay
 * Uses viewBox matching photo aspect ratio for percentage-based coordinates
 */
export function MarkupCanvas({
  shapes,
  selectedShapeId,
  onShapeClick,
  onBackgroundClick,
  onShapeMove,
  readOnly = false,
  photoAspectRatio = 1,
}: MarkupCanvasProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  // Use shared drag handler for shapes and handles
  const {
    cursor: dragCursor,
    handleMouseDown: dragHandleMouseDown,
    handleMouseMove: dragHandleMouseMove,
    handleMouseUp: dragHandleMouseUp,
    handleMouseLeave: dragHandleMouseLeave,
  } = useMarkupDrag({
    shapes,
    selectedShapeId,
    onShapeClick,
    onShapeMove,
    containerRef: svgRef,
    photoAspectRatio,
    readOnly,
  });

  // Calculate viewBox dimensions to match photo aspect ratio
  const viewBoxHeight = 100 / photoAspectRatio;
  const viewBox = `0 0 100 ${viewBoxHeight}`;

  /**
   * Handle click on SVG background (deselect shapes)
   */
  const handleBackgroundClick = (e: React.MouseEvent<SVGSVGElement>) => {
    // Only deselect if clicking directly on SVG (not on a shape)
    if (e.target === e.currentTarget && onBackgroundClick) {
      onBackgroundClick();
    }
  };

  /**
   * Handle mouse down on shape or handle - delegate to shared drag handler
   */
  const handleShapeMouseDown = (e: React.MouseEvent<SVGElement>) => {
    if (readOnly) return;
    e.stopPropagation();
    dragHandleMouseDown(e);
  };

  // Sort shapes by z-order (lower z-order renders first/behind, higher z-order renders last/in front)
  const sortedShapes = [...shapes].sort((a, b) => {
    const aZOrder = getShapeZOrder(a);
    const bZOrder = getShapeZOrder(b);
    return aZOrder - bZOrder;
  });

  // Use drag cursor with fallback to default
  const cursor = dragCursor ?? 'default';

  return (
    <svg
      ref={svgRef}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: readOnly ? 'none' : 'auto',
        cursor,
      }}
      onClick={handleBackgroundClick}
      onMouseMove={dragHandleMouseMove}
      onMouseUp={dragHandleMouseUp}
      onMouseLeave={dragHandleMouseLeave}
    >
      {sortedShapes.map((shape) => {
        const isSelected = shape.number === selectedShapeId;

        // BUG FIX (#480): Transform shape coordinates from image space (0-100, 0-100)
        // to viewBox space (0-100, 0-viewBoxHeight) for correct rendering
        const transformedShape = transformShapeForViewBox(shape, photoAspectRatio);

        // Render shape using tool registry
        const element = renderShape({
          shape: transformedShape,
          isSelected,
          onMouseDown: handleShapeMouseDown,
          viewBoxHeight,
        });

        // Clone element with key prop for React list rendering
        return element ? React.cloneElement(element, { key: `shape-${shape.number}` }) : null;
      })}

      {/* Grab handles for selected shape */}
      {selectedShapeId !== null && (() => {
        const selectedShape = shapes.find(s => s.number === selectedShapeId);
        if (!selectedShape || readOnly) return null;

        // BUG FIX (#480): Transform selected shape before getting handles
        const transformedSelectedShape = transformShapeForViewBox(selectedShape, photoAspectRatio);
        const handles = getShapeHandles(transformedSelectedShape);
        const handleRadius = 1; // Radius for grab handles

        return handles.map((handle) => (
          <circle
            key={`${selectedShape.number}-${handle.id}`}
            cx={handle.x}
            cy={handle.y}
            r={handleRadius}
            fill="white"
            stroke="cyan"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
            style={{ cursor: handle.cursor, pointerEvents: 'auto' }}
            onMouseDown={handleShapeMouseDown}
          >
            <title>{handle.label}</title>
          </circle>
        ));
      })()}

      {/* Selection glow effect */}
      {selectedShapeId && (
        <defs>
          <filter id="selection-glow">
            <feGaussianBlur stdDeviation="1" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      )}

      <style>{`
        .markup-shape {
          transition: stroke-width 0.1s ease;
        }
        .markup-shape.selected {
          filter: drop-shadow(0 0 2px rgba(59, 130, 246, 0.6));
        }
        .markup-shape:hover:not(.selected) {
          stroke-width: 1.2 !important;
        }
      `}</style>
    </svg>
  );
}
